import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { apiClient, apiErrorMessage } from "../api/client";
import type { Campaign } from "../api/types";
import { campaignPath } from "../lib/campaign";
import { absent, shortDate } from "../lib/format";
import { sortRows } from "../lib/sortRows";
import { useListParams } from "../lib/useListParams";
import { DataTable, type ColumnSpec } from "../ui/DataTable";
import { Spinner } from "./Spinner";

/* Coming up, on the Enrichment page: the campaigns in Planning that chose
 * manual enrichment and have a date or a place in a sequence, so the
 * researcher sees work before it reaches her and can work ahead (James,
 * 2026-10-19 and 2026-11-02, engine docs/plans/campaign-scheduling.md and
 * campaign-sequencing.md). They open in the engine's order of the work
 * (enrichment_rank, engine migration 313), with its sentence under each name.
 * Each row is one campaign with when its enrichment starts, when calling
 * starts, who owns it and the first line of its research notes, and the
 * engine's sentence in red under its name when its web lookups would finish
 * after its calling date. A planned campaign is a few rows, so the list is
 * read whole and sorted on the screen. Every fact is api.campaign's. */
const COLUMNS = "name,label,enrich_on,call_on,owner_name,research,calling_words,calling_warning,sequence_length,enrichment_rank,enrichment_words";

function firstLine(research: unknown): string {
  const notes = research && typeof research === "object" ? (research as { notes?: unknown }).notes : null;
  return typeof notes === "string" ? (notes.split("\n").find((l) => l.trim()) ?? "").trim() : "";
}

export function EnrichmentComingUp({ token }: { token: string }) {
  const params = useListParams("enrichment_rank", [], true, "", "cu.");
  const q = useQuery({
    queryKey: ["enrichment-coming-up"],
    queryFn: async () => {
      const { data, error } = await apiClient(token).from("campaign").select(COLUMNS)
        .is("archived_at", null).eq("stage", "draft").eq("ask_enricher", true)
        .or("enrich_on.not.is.null,call_on.not.is.null,sequence_length.gt.1")
        .order("enrichment_rank", { ascending: true, nullsFirst: false })
        .limit(200);
      if (error) throw new Error(apiErrorMessage(error));
      return (data ?? []) as Campaign[];
    },
  });

  const rows = sortRows(q.data ?? [], params.sorts, (c, column) => {
    switch (column) {
      case "enrichment_rank": return c.enrichment_rank;
      case "label": return c.label;
      case "enrich_on": return c.enrich_on;
      case "call_on": return c.call_on;
      case "owner_name": return c.owner_name;
      default: return null;
    }
  }, [{ column: "enrichment_rank", ascending: true }]);

  const columns: ColumnSpec<Campaign>[] = [
    { id: "campaign", header: "Campaign", role: "leading", fields: [], sort: "label", title: true, narrowLabel: null,
      cell: (c) => (
        <>
          <Link to={campaignPath(c.name ?? "")}>{c.label ?? c.name}</Link>
          {c.calling_warning && c.calling_words ? <div className="refusal">{c.calling_words}</div>
            : c.enrichment_words ? <div className="muted">{c.enrichment_words}</div> : null}
        </>
      ) },
    { id: "enrich", header: "Enrichment starts", role: "secondary", fields: [], sort: "enrich_on", narrowLabel: "enrichment starts",
      cell: (c) => c.enrich_on ? shortDate(c.enrich_on) : absent("Not set", "No date. A person starts it.") },
    { id: "calling", header: "Estimated start", role: "secondary", fields: [], sort: "call_on", narrowLabel: "estimated start",
      cell: (c) => c.call_on ? shortDate(c.call_on) : absent("Not set", "No estimated starting date") },
    { id: "owner", header: "Owner", role: "secondary", fields: [], sort: "owner_name",
      cell: (c) => c.owner_name ?? <span className="muted">None</span> },
    { id: "notes", header: "Research notes", role: "detail", fields: [], narrowLabel: "notes",
      cell: (c) => firstLine(c.research) || <span className="muted">None</span> },
  ];

  return (
    <section className="ecu" aria-labelledby="coming-up">
      <h2 id="coming-up">Coming up</h2>
      {q.isLoading ? <p className="muted"><Spinner />Loading.</p>
        : q.isError ? <p className="refusal" role="alert">{(q.error as Error).message}</p>
        : !rows.length ? <p className="muted">No planned campaign with a date or a place in a sequence asks for manual enrichment.</p>
        : <DataTable columns={columns} rows={rows} rowKey={(c) => c.name ?? ""} params={params} />}
    </section>
  );
}
